import { alpha, Button, Grid, Typography, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import { useLocation, useNavigate } from 'react-router';

import { Iconify, Scrollbar, useResponsive } from '@zeta-core';
import { RegistrationBottomIllustration, RegistrationTopIllustration, ZetaLogo } from '@zeta-core/assets';
import { NAV } from '../config-layout';

type Props = {
  openNav: boolean;
};

interface IRegistrationSteps {
  stepIcon: string;
  stepName: string;
  stepActivated: boolean;
  index: number;
}

const registrationSteps = [
  {
    stepName: 'Registration',
    stepCaption: 'Please complete the below sections to register with Zeta.',
    stepIcon: 'ci:edit-pencil-line-02',
    stepPathnames: ['/registration/main/personal-details', '/registration/main/verification', '/registration/main/password-set-up'],
  },
  {
    stepName: 'Verification',
    stepCaption: 'Complete the email or SMS verification Process',
    stepIcon: 'mdi:tick-circle-outline',
    stepPathnames: ['/registration/main/verification', '/registration/main/password-set-up'],
  },
  {
    stepName: 'Username & Password',
    stepCaption: 'Please set up password and you are ready to start using Zeta',
    stepIcon: 'tabler:circle-key',
    stepPathnames: ['/registration/main/password-set-up'],
  },
];

const RegistrationSteps = ({ stepIcon, stepName, stepActivated, index }: IRegistrationSteps) => {
  const theme = useTheme();
  return (
    <Grid
      container
      spacing={2}
      sx={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Grid>
        <Box display="flex" alignItems="center" height="100%" width="100%">
          <Stack
            sx={{
              background: stepActivated ? theme.palette.primary.main : theme.palette.grey[800],
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: 64,
              width: 32,
              height: 32,
              position: 'relative',
              '&:before': {
                content: '""',
                display: index !== 0 ? 'block' : 'none',
                position: 'absolute',
                height: '40px',
                width: '2px',
                top: '-40px',
                left: '15px',
                backgroundColor: alpha(theme.palette.grey[500], 0.12),
              },
            }}
          >
            <Iconify icon={stepIcon} width={18} color={stepActivated ? theme.palette.primary.contrastText : theme.palette.grey[600]} />
          </Stack>
        </Box>
      </Grid>
      <Grid
        pl={0}
        size={{
          xs: 10,
          md: 10,
          lg: 10,
          xl: 10,
        }}
      >
        <Stack justifyContent="center">
          <Typography variant="h6" color={stepActivated ? theme.palette.primary.contrastText : theme.palette.grey[600]}>
            {stepName}
          </Typography>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default function NavVertical({ openNav }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();

  const lgUp = useResponsive('up', 'lg');

  const { pathname } = useLocation();

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack
        p={7}
        height="100vh"
        sx={{
          background: theme.palette.grey[900],
          overflowY: 'auto',
          position: 'relative',
        }}
        justifyContent="space-between"
        direction="column"
      >
        <Box
          sx={{
            position: 'absolute',
            right: '-55px',
            top: '-65px',
            zIndex: 1000,
            transform: 'rotate(90deg)',
          }}
        >
          <RegistrationTopIllustration />
        </Box>
        <Box
          sx={{
            position: 'absolute',
            left: '-75px',
            bottom: '60px',
            zIndex: 1000,
            transform: 'rotate(-90deg)',
          }}
        >
          <RegistrationBottomIllustration />
        </Box>

        <Stack gap={5}>
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <ZetaLogo />
            </Box>

            <Stack marginTop={5}>
              <Typography variant="h3" color="background.default">
                Onboarding
              </Typography>
            </Stack>

            <Box>
              <Typography variant="body2" color="text.disabled">
                Please complete the below sections to onboard with Zeta.
              </Typography>
            </Box>
          </Box>

          {registrationSteps.map((step, index) => (
            <Stack key={step.stepName}>
              <RegistrationSteps
                key={step.stepName}
                stepIcon={step.stepIcon}
                stepName={step.stepName}
                stepActivated={step.stepPathnames.some((path) => pathname.includes(path))}
                index={index}
              />
            </Stack>
          ))}
        </Stack>

        <Stack alignItems="center" justifyContent="center" direction="row">
          <Typography variant="body1" color="text.disabled">
            Already have an account?
          </Typography>
          <Button variant="text" color="primary" onClick={() => navigate('/auth/login')}>
            Login
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_VERTICAL_ONBOARDING },
      }}
    >
      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.W_VERTICAL_ONBOARDING,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          slotProps={{
            paper: {
              sx: {
                width: NAV.W_VERTICAL_ONBOARDING,
              },
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
